import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Zap, AlertCircle, Search, Filter, Image, Video, Headphones } from 'lucide-react';
import { supabase } from '../../utils/supabaseStorage';
import { CachedImage } from '../CachedImage';
import { VideoThumbnail } from '../VideoThumbnail';
import { LoadingSpinner } from '../LoadingSpinner';
import { MediaPreviewModal } from '../MediaPreviewModal';
import { getAssetMediaType } from '../../utils/formPreviewHelpers';

interface Asset {
  id: string;
  public_url: string;
  filename?: string;
  media_type?: 'image' | 'video' | 'audio';
  file_extension?: string;
  created_at: string;
  hy_user_id: string;
  hy_name?: string;
  generation_prompt?: string;
  generation_params?: any;
}

interface AppAssetsModalProps {
  isOpen: boolean;
  onClose: () => void;
  appId: string;
  appName: string;
  assetCount: number;
}

type FilterType = 'all' | 'image' | 'video' | 'audio';

export const AppAssetsModal = ({ isOpen, onClose, appId, appName, assetCount }: AppAssetsModalProps) => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null);
  const observerRef = useRef<HTMLDivElement>(null);

  const ASSETS_PER_PAGE = 20;

  const fetchAssets = async (offset: number = 0, append: boolean = false) => {
    try {
      if (offset === 0) setLoading(true);
      setError(null);

      console.log('📊 [APP-ASSETS] Fetching assets for app:', appId);

      // Fetch assets for specific app with user info
      const { data: assetsData, error: assetsError } = await supabase
        .from('assets')
        .select(`
          id,
          public_url,
          filename,
          media_type,
          file_extension,
          created_at,
          hy_user_id,
          generation_prompt,
          generation_params,
          users!inner(hy_name)
        `)
        .eq('app_id', appId)
        .eq('isaigenerated', true)
        .order('created_at', { ascending: false })
        .range(offset, offset + ASSETS_PER_PAGE - 1);

      if (assetsError) {
        throw assetsError;
      }

      if (!assetsData || assetsData.length === 0) {
        setHasMore(false);
        if (offset === 0) setAssets([]);
        return;
      }

      // Transform data to include user name
      const enrichedAssets = assetsData.map(asset => ({
        ...asset,
        hy_name: (asset.users as any)?.hy_name || 'Unknown User'
      }));

      if (append) {
        setAssets(prev => [...prev, ...enrichedAssets]);
      } else {
        setAssets(enrichedAssets);
      }

      setCurrentOffset(offset + ASSETS_PER_PAGE);
      setHasMore(assetsData.length === ASSETS_PER_PAGE);

    } catch (err) {
      console.error('Error fetching app assets:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch assets');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreAssets = useCallback(() => {
    if (hasMore && !loading) {
      fetchAssets(currentOffset, true);
    }
  }, [hasMore, loading, currentOffset, appId]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMoreAssets();
        }
      },
      { threshold: 0.1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [loadMoreAssets, hasMore, loading]);

  // Load initial assets when modal opens
  useEffect(() => {
    if (isOpen && appId) {
      setAssets([]);
      setCurrentOffset(0);
      setHasMore(true);
      setError(null);
      setSearchTerm('');
      setFilter('all');
      fetchAssets(0, false);
    }
  }, [isOpen, appId]);

  // Filter and search assets
  const filteredAssets = assets.filter(asset => {
    // Type filter
    let matchesType = true;
    if (filter !== 'all') {
      const assetType = getAssetMediaType(asset);
      matchesType = assetType === filter;
    }

    // Search filter
    const matchesSearch = !searchTerm.trim() ||
      asset.filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.generation_prompt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.hy_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.id.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesType && matchesSearch;
  });

  const renderAssetItem = (asset: Asset) => {
    const mediaType = getAssetMediaType(asset);

    return (
      <div key={asset.id} className="break-inside-avoid mb-4">
        <div
          className="bg-elevated rounded-xl overflow-hidden shadow-lg hover:scale-[1.02] transition-all cursor-pointer"
          onClick={() => setPreviewAsset(asset)}
        >
          {/* Asset Preview */}
          <div className="relative bg-surface">
            {/* Media Type Badge */}
            <div className="absolute top-2 left-2 z-10">
              <div className="px-2 py-1 bg-black/60 backdrop-blur-sm rounded-full text-xs font-medium text-white flex items-center space-x-1">
                {mediaType === 'image' && <Image className="w-3 h-3" />}
                {mediaType === 'video' && <Video className="w-3 h-3" />}
                {mediaType === 'audio' && <Headphones className="w-3 h-3" />}
                <span className="uppercase">{mediaType}</span>
              </div>
            </div>

            {mediaType === 'image' ? (
              <CachedImage
                src={asset.public_url}
                alt="App asset"
                className="w-full h-auto object-cover"
                fallback={
                  <div className="w-full h-32 flex items-center justify-center text-th-secondary bg-elevated">
                    <Image className="w-8 h-8" />
                  </div>
                }
              />
            ) : mediaType === 'video' ? (
              <div className="w-full">
                <VideoThumbnail
                  src={asset.public_url}
                  className="w-full"
                  showPlayButton={true}
                  onClick={() => setPreviewAsset(asset)}
                />
              </div>
            ) : (
              <div
                className="w-full bg-status-warning/20 flex items-center justify-center"
                style={{ aspectRatio: '16/9' }}
              >
                <Headphones className="w-8 h-8 text-status-warning" />
              </div>
            )}
          </div>

          {/* Asset Info */}
          <div className="p-3 space-y-2">
            <p className="text-sm font-medium text-th-primary truncate">
              {asset.filename || `Asset ${asset.id.slice(0, 8)}`}
            </p>
            <div className="flex items-center justify-between text-xs text-th-secondary">
              <span className="truncate">{asset.hy_name}</span>
              <span>{new Date(asset.created_at).toLocaleDateString()}</span>
            </div>
            {asset.generation_prompt && (
              <p className="text-xs text-th-secondary line-clamp-2">
                {asset.generation_prompt}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Filter options with counts
  const filterOptions = [
    {
      id: 'all',
      label: 'All',
      icon: Zap,
      count: assets.length
    },
    {
      id: 'image',
      label: 'Images',
      icon: Image,
      count: assets.filter(a => getAssetMediaType(a) === 'image').length
    },
    {
      id: 'video',
      label: 'Videos',
      icon: Video,
      count: assets.filter(a => getAssetMediaType(a) === 'video').length
    },
    {
      id: 'audio',
      label: 'Audio',
      icon: Headphones,
      count: assets.filter(a => getAssetMediaType(a) === 'audio').length
    }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-base z-modal-content flex flex-col">
      {/* Header */}
      <div className="bg-surface border-b border-subtle">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 text-th-secondary hover:text-th-primary transition-colors rounded-full hover:bg-overlay"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex-1 text-center">
            <h2 className="text-lg font-semibold text-th-primary">{appName}</h2>
            <p className="text-xs text-th-secondary">
              {assetCount.toLocaleString()} AI-generated assets
            </p>
          </div>

          <div className="w-8 h-8" /> {/* Spacer for centering */}
        </div>

        {/* Search Bar */}
        <div className="px-4 pb-3">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-th-secondary" />
            </div>
            <input
              type="text"
              placeholder="Search assets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-3 bg-elevated/50 border border-subtle/30 rounded-xl text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20 transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-th-secondary hover:text-th-primary transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Filter Chips */}
        <div className="px-4 pb-4">
          <div className="flex items-center space-x-2 overflow-x-auto scrollbar-hide">
            <Filter className="w-4 h-4 text-th-secondary flex-shrink-0" />
            {filterOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => setFilter(option.id as FilterType)}
                className={`flex items-center space-x-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                  filter === option.id
                    ? 'bg-brand text-surface shadow-lg'
                    : 'bg-elevated/50 text-th-secondary hover:text-th-primary hover:bg-elevated border border-subtle/30'
                }`}
              >
                <option.icon className="w-3.5 h-3.5" />
                <span>{option.label}</span>
                {option.count > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                    filter === option.id
                      ? 'bg-white/20 text-white'
                      : 'bg-brand/20 text-brand'
                  }`}>
                    {option.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading && assets.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <LoadingSpinner size="lg" />
              <p className="text-th-secondary">Loading assets...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-4 max-w-md px-4">
              <AlertCircle className="w-16 h-16 text-status-error mx-auto" />
              <h3 className="text-xl font-bold text-th-primary">Failed to Load Assets</h3>
              <p className="text-th-secondary">{error}</p>
              <button
                onClick={() => fetchAssets(0, false)}
                className="button-primary"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-4 px-4">
              <div className="w-20 h-20 bg-elevated/50 rounded-full flex items-center justify-center mx-auto">
                <Zap className="w-10 h-10 text-th-secondary" />
              </div>
              <h3 className="text-xl font-bold text-th-primary">
                {searchTerm ? 'No Assets Found' : 'No Assets Yet'}
              </h3>
              <p className="text-th-secondary">
                {searchTerm
                  ? `No assets match "${searchTerm}" in this app`
                  : `This app hasn't generated any ${filter === 'all' ? '' : filter + ' '}assets yet`
                }
              </p>
            </div>
          </div>
        ) : (
          <div className="p-4">
            {/* Native-style Grid */}
            <div className="columns-2 md:columns-3 lg:columns-4 gap-4">
              {filteredAssets.map(asset => renderAssetItem(asset))}
            </div>

            {/* Loading More Indicator */}
            {loading && assets.length > 0 && (
              <div className="text-center py-8">
                <LoadingSpinner size="md" />
                <p className="text-th-secondary mt-2">Loading more assets...</p>
              </div>
            )}

            {/* End of Results */}
            {!hasMore && assets.length > 0 && (
              <div className="text-center py-8">
                <p className="text-th-secondary">
                  All {assets.length} asset{assets.length !== 1 ? 's' : ''} loaded
                </p>
              </div>
            )}

            {/* Intersection Observer Target */}
            {hasMore && (
              <div ref={observerRef} className="h-10 flex items-center justify-center">
                <div className="w-2 h-2 bg-brand rounded-full animate-pulse"></div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Stats Bar */}
      <div className="bg-surface border-t border-subtle">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between text-sm">
            <div className="text-th-secondary">
              Showing {filteredAssets.length} of {assets.length} assets
              {searchTerm && ` matching "${searchTerm}"`}
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-brand rounded-full"></div>
              <span className="text-brand font-medium">App ID: {appId}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Media Preview Modal */}
      {previewAsset && (
        <MediaPreviewModal
          isOpen={!!previewAsset}
          onClose={() => setPreviewAsset(null)}
          asset={previewAsset}
          onDownload={() => {
            const link = document.createElement('a');
            link.href = previewAsset.public_url;
            link.download = previewAsset.filename || `asset-${previewAsset.id}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
        />
      )}

      {/* Background Effects */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      </div>
    </div>
  );
};
